import React, { useEffect, useRef, useState } from "react";
import { sponsorRegistration } from "../../../../../core/data/json/contractFarming";

interface BlankField {
  blankVal: string;
  blankType: string;
  blankName: string;
}

interface TableItem {
  tableHeading: string;
  tableType: string;
  tableVal: string;
}

interface ConditionItem {
  itemNo: string;
  item: string;
  isBlankExist: boolean;
  blank: BlankField[];
  isTableExist: boolean;
  tableItems: TableItem[];
  subItem: any[];
  // optional status to support MB color coding
  // 'added' | 'updated' | 'deleted' | 'normal'
  // kept optional to avoid typing issues when not present
  status?: string;
}

const ConditionRenderer = ({ row, conditionsData, index, checked, onConditionsChange, roleIdentifier, isEditing, isReadOnly, condFields, setCondFields, setFormErrors }: { row: ConditionItem; conditionsData: any, index: any, checked: any, onConditionsChange: any, roleIdentifier?: string, isEditing?: boolean, isReadOnly?: boolean, condFields: any, setCondFields: any, setFormErrors:any }) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const containerRef = useRef<HTMLDivElement | null>(null);
  const initialized = useRef(false); // prevents re-rendering innerHTML again



  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // 🧩 STEP 1 — Parse and inject stored values before first render
    const parser = new DOMParser();
    const doc = parser.parseFromString(row.item || "", "text/html");
    const inputs = doc.querySelectorAll("input");

    inputs.forEach((input) => {
      const name = input.getAttribute("name");
      if (name) {
        let value = (row as any)?.[name] || "";

        if (name && Object.keys(condFields).includes(name)) {
          setCondFields((prev: any) => ({
            ...prev,
            [name]: value
          }));
        }

        // 👇 Pre-fill input value from saved object data (even if empty)
        input.setAttribute("value", value);
        // Remove any hardcoded numeric text node directly following the input (legacy remnants like " 12")
        const next = input.nextSibling;
        if (next && next.nodeType === Node.TEXT_NODE) {
          const txt = next.textContent || "";
          if (/^\s*\d+\s*/.test(txt)) {
            next.textContent = txt.replace(/^\s*\d+\s*/, "");
          }
        }
      }

      setFormErrors((prevErrors: any) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[name];
        return updatedErrors;
      });

    });

   

    // 🧩 STEP 2 — Inject HTML only once when first rendered
    if (!initialized.current) {
      container.innerHTML = doc.body.innerHTML;
      initialized.current = true;
    }

    // 🧩 STEP 3 — After HTML is injected, set values on actual DOM inputs and disable if read-only
    const liveInputs = container.querySelectorAll("input");
    liveInputs.forEach((input: HTMLInputElement) => {
      const name = input.getAttribute("name");
      if (name) {
        const value = (row as any)?.[name] || "";
        input.value = value; // Set the value on the live DOM element
        // Always apply disabled state based on isReadOnly
        input.disabled = !!isReadOnly;
        if (isReadOnly) {
          input.style.cursor = "not-allowed";
          input.style.backgroundColor = "#f5f5f5";
        } else {
          input.style.cursor = "text";
          input.style.backgroundColor = "";
        }
      }
    });

    // 🧩 STEP 4 — Listen for input change (only if not read-only)
    if (!isReadOnly) {
      // Validation helper for numeric inputs in conditions
      const validateNumericInput = (value: string): string => {
        // Remove any non-numeric characters (only allow digits)
        return value.replace(/[^\d]/g, '');
      };

      const handleInputChange = (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (target && target.name) {
          let inputValue = target.value;

          // Apply numeric validation for "Enter Period of Months" fields
          if (target.name === 'periodOfMonths' || target.name === 'periodOfMonths2') {
            inputValue = validateNumericInput(target.value);
            // Update the input field value immediately
            target.value = inputValue;
          }

          const updatedValue = {
            ...formData,
            [target.name]: inputValue,
          };
          setFormData(updatedValue);

          // Save value into parent data array (defensive)
          if (Array.isArray(conditionsData) && conditionsData[index]) {
            conditionsData[index][target.name] = inputValue;
          }
        }
      };

      container.addEventListener("input", handleInputChange);
      return () => container.removeEventListener("input", handleInputChange);
    }
  }, [row.item, formData, row, isReadOnly]);

  const handleClicked = () => {
    // Keep inputs editable in all modes; do not replace inputs with text
    return;
  };

  useEffect(() => {
    handleClicked();
  }, [checked, roleIdentifier])

  // Additional effect to update disabled state when isReadOnly changes
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const liveInputs = container.querySelectorAll("input");
    liveInputs.forEach((input: HTMLInputElement) => {
      input.disabled = !!isReadOnly;
      if (isReadOnly) {
        input.style.cursor = "not-allowed";
        input.style.backgroundColor = "#f5f5f5";
      } else {
        input.style.cursor = "text";
        input.style.backgroundColor = "";
      }
    });
  }, [isReadOnly]);

  // Remove inner status-based background everywhere; outer row provides the darker status color
  const statusStyle: React.CSSProperties = {};

  const handleContentEdit = (e: React.FormEvent<HTMLDivElement>) => {
    if (!isEditing || isReadOnly) return;
    const updatedHTML = e.currentTarget.innerHTML;
    if (Array.isArray(conditionsData) && conditionsData[index]) {
      conditionsData[index].item = updatedHTML;
    }
  };

  return (
    <div className="p-3 mb-3" style={statusStyle}>
      {/* Dynamic HTML content - make editable only when in edit mode AND not read-only */}
      <div
        ref={containerRef}
        className="condition-item"
        contentEditable={isEditing && !isReadOnly}
        onBlur={handleContentEdit}
        suppressContentEditableWarning={true}
        style={isEditing && !isReadOnly ? {
          border: '1px solid #d3d3d3',
          padding: '10px',
          borderRadius: '5px',
          outline: 'none'
        } : {}}
      />
    </div>
  );
};

export default ConditionRenderer;

